import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "../../firebase";
import { Search, X, Clock, ArrowRight } from "lucide-react";
import { formatPrice } from "../../utils/format";

const HISTORY_KEY = "afriport_search_history";
const MAX_HISTORY = 6;

export default function SearchBar({ compact = false }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []; } catch { return []; }
  });
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handler(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced search
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!term.trim()) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, "products"), where("status", "==", "active"), limit(50)));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const q = term.toLowerCase();
        const filtered = all.filter(p =>
          p.name?.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q) ||
          p.subcategory?.toLowerCase().includes(q) ||
          p.brand?.toLowerCase().includes(q) ||
          (p.tags || []).some(t => t.toLowerCase().includes(q))
        ).slice(0, 6);
        setResults(filtered);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [term]);

  function saveHistory(q) {
    const trimmed = q.trim();
    if (!trimmed) return;
    const updated = [trimmed, ...history.filter(h => h !== trimmed)].slice(0, MAX_HISTORY);
    setHistory(updated);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  }

  function handleSearch(q) {
    const searchTerm = q || term;
    if (!searchTerm.trim()) return;
    saveHistory(searchTerm);
    setOpen(false);
    setTerm(searchTerm);
    navigate(`/shop?search=${encodeURIComponent(searchTerm.trim())}`);
  }

  function handleProductClick(id) {
    saveHistory(term);
    setOpen(false);
    navigate(`/product/${id}`);
  }

  function removeHistory(item, e) {
    e.stopPropagation();
    const updated = history.filter(h => h !== item);
    setHistory(updated);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  }

  const showDropdown = open && (term.length > 0 || history.length > 0);

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: compact ? undefined : 1, maxWidth: compact ? 340 : 540, width: "100%" }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <Search size={16} style={{ position: "absolute", left: 12, color: "var(--text-muted)", pointerEvents: "none" }} />
        <input
          className="form-input"
          placeholder="Search inverters, Starlinks, laptops…"
          style={{ paddingLeft: 38, paddingRight: term ? 36 : 12, borderRadius: 100 }}
          value={term}
          onChange={e => setTerm(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
        />
        {term && (
          <button onClick={() => { setTerm(""); setResults([]); }}
            style={{ position: "absolute", right: 10, border: "none", background: "transparent", cursor: "pointer", color: "var(--text-muted)", display: "flex" }}>
            <X size={15} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="search-dropdown">
          {/* Recent searches */}
          {!term && history.length > 0 && (
            <div style={{ padding: "12px 16px 8px" }}>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: 8 }}>Recent Searches</p>
              {history.map(h => (
                <div key={h} onClick={() => handleSearch(h)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 4px", cursor: "pointer", borderRadius: 6 }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                    <Clock size={14} color="var(--text-muted)" /> {h}
                  </div>
                  <button onClick={e => removeHistory(h, e)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-muted)", display: "flex" }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && term && (
            <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>Searching…</div>
          )}

          {/* Results */}
          {!loading && term && results.length > 0 && (
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", padding: "12px 16px 4px" }}>Products</p>
              {results.map(p => (
                <div key={p.id} onClick={() => handleProductClick(p.id)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <div style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", background: "var(--surface)", flexShrink: 0, border: "1px solid var(--border)" }}>
                    {p.images?.[0] ? <img src={p.images[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>📦</div>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</div>
                  </div>
                  <div style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14, color: "var(--brand)", flexShrink: 0 }}>{formatPrice(p.price)}</div>
                </div>
              ))}
              <div onClick={() => handleSearch()} style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--border)", cursor: "pointer", fontSize: 14, color: "var(--brand)", fontWeight: 500 }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--brand-light)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                <ArrowRight size={14} /> See all results for "{term}"
              </div>
            </div>
          )}

          {/* No results */}
          {!loading && term && results.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>
              No products found for "<strong>{term}</strong>"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
